import { auth } from "../../firebaseConfig";
import {
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { getDocumentRef, getCollectionRef } from "../utils";
import { firebaseGetCurrentPerson } from "../admins/adminApi";

/* global grecaptcha */

/**
 *create and invisible recaptacha and return it
 * @param {string} recaptachContainerId id of the div where the recaptcha is bing displayed
 * @returns
 */
const generateRecaptchaVerifier = (recaptachContainerId) => {
  //create and invisible recaptcha
  let verifier = new RecaptchaVerifier(
    recaptachContainerId,
    {
      size: "invisible",
      callback: (response) => {
        // reCAPTCHA solved, allow signInWithPhoneNumber.
      },
    },
    auth
  );

  return verifier;
};

/**
 * Send the OTP code to the user phone number
 * @param {String} phoneNumber
 * @param {String} recaptchaVerifier a recaptcha verifier object
 * @returns confirmationResult
 */
const sendOptCodeToTheUser = async (phoneNumber, recaptchaVerifier) => {
  try {
    //sigin user with phone number
    const confirmationResult = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      recaptchaVerifier
    );

    if (confirmationResult) {
      return { data: confirmationResult };
    }

    return { error: "Something went wrong" };
  } catch (err) {
    //reset the recaptcha verifier so that the user can try to sign again

    console.log(err);
    return {
      error: "And error occured while signing the opt code",
    };
  }
};

/**
 * Get the user on firebase database and return it
 * @param {String} userId
 * @returns
 */
const firebaseGetUser = async (userId) => {
  //reference to the user's document
  const userRef = getDocumentRef("users", userId);

  try {
    const doc = await getDoc(userRef);

    if (!doc.exists())
      return { error: "User don't exist on firebase database" };

    const user = { ...doc.data(), id: doc.id };
    return { data: user };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while geting the user" };
  }
};

/**
 * add user on firebase with id alrady exists
 * @param {String} idUser
 * @param {Object} userData
 * @returns
 */
const firebaseAddUser = async (idUser, userData) => {
  //create a reference to the user with a custom id
  const userRef = getDocumentRef("users", idUser);

  try {
    await setDoc(userRef, userData);

    return { data: true };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while adding the user" };
  }
};

/**
 * sign in the user with the otp code
 * @param {number} otpCode the otp code sent by sms to the user
 * @param {Object} confirmationResult the object that allow user to sign in with phone number
 * @returns
 */
const siginUserWithOtpCode = async (
  otpCode,
  confirmationResult,
  phoneNumber
) => {
  try {
    const result = await confirmationResult.confirm(otpCode);

    if (result) {
      const uid = result.user.uid;

      //check if user alray exist on firestore
      const { data, error } = await firebaseGetUser(uid);

      if (data) return { data };

      //create user on firestore with the default profile
      const DEFAULT_DATA = {
        role: 0,
        phone: phoneNumber,
        name: "Monsieur xyz",
        isAlreadyContactedByAdmin: false,
        savedProducts: [],
        image:
          "https://firebasestorage.googleapis.com/v0/b/nti-shop.appspot.com/o/admins%2Fdefault-admin-image.jpg?alt=media&token=41048904-008a-4ca2-8293-5a853083e693",
      };

      const { data: data2, error: error2 } = await firebaseAddUser(
        uid,
        DEFAULT_DATA
      );

      console.log();

      if (data2) return { data: data2 };
    }
    return { error: "Something went wrong" };
  } catch (err) {
    console.log(err);
    return {
      error: "And error occured while signing in the user with phone number",
    };
  }
};

/**
 * update the user on firebase database
 * @param {string} idUser
 * @param {Object} data
 * @returns
 */
const updateUser = async (idUser, data) => {
  //ref to the user to update
  const userRef = getDocumentRef("users", idUser);

  try {
    //update the user
    await updateDoc(userRef, { ...data });

    return { data: true };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while updating the user" };
  }
};

/**
 *
 * @param {*} idUser
 * @param {*} globaStateLogin
 * @returns
 */
const getUserWithUpdatedData = async (
  idUser,
  globaStateLogin = (data) => {}
) => {
  //ref to the user to listen
  const userRef = getDocumentRef("users", idUser);

  try {
    onSnapshot(userRef, async (doc) => {
      await firebaseGetCurrentPerson(globaStateLogin);
    });
  } catch (err) {
    console.log(err);
    return { error: "And error occured while getting the updated user" };
  }
};

/**
 * check if the product already exist on the old user's saved products
 * @param {Object} productRef
 * @param {Array} listProductsRefToCheck
 * @returns
 */
const checkProductAlreadySaved = (productRef, listProductsRefToCheck) => {
  const prodoctId = productRef.id;
  const listProductsIdToCheck = listProductsRefToCheck.map((p) => p.id);

  return listProductsIdToCheck.includes(prodoctId);
};

/**
 *Save a specific product to the user
 * @param {String} idUser
 * @param {String} idProduct
 * @returns data | error
 */
const saveProduct = async (user, idProduct) => {
  //ref to the user to update
  const userRef = getDocumentRef("users", user.getId);

  //ref to the product to save
  const productRef = getDocumentRef("products", idProduct);

  //get the reference to old saved products
  const oldSavedProductsRef = user.getSavedProducts.map((p) =>
    getDocumentRef("products", p.getId)
  );

  //check if the product is already saved
  const isAlreadySaved = checkProductAlreadySaved(
    productRef,
    oldSavedProductsRef
  );

  if (isAlreadySaved) return { data: false };

  try {
    //save a new product to the user
    await updateDoc(userRef, {
      savedProducts: [...oldSavedProductsRef, productRef],
    });

    return { data: true };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while saving the product" };
  }
};

/**
 * Unsave a specific product to the user
 * @param {Object} user
 * @param {String} idProduct
 * @returns data | error
 */
const unsaveProduct = async (user, idProduct) => {
  //ref to the user to update
  const userRef = getDocumentRef("users", user.getId);

  //get the id to old saved products
  const oldSavedProductsId = user.getSavedProducts.map((p) => p.getId);

  //remove id of the product to unsave
  const savedProductsFilteredId = oldSavedProductsId.filter(
    (id) => id !== idProduct
  );

  //get the new list of reference to the products to save
  const savedProductsRef = savedProductsFilteredId.map((id) =>
    getDocumentRef("products", id)
  );

  try {
    //save a new product to the user
    await updateDoc(userRef, {
      savedProducts: [...savedProductsRef],
    });

    return { data: true };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while unsaving the product" };
  }
};

/**
 * get all users that does not contacted by admin
 * @returns  data | error
 */
const getUserNoContactedByAdmin = async (globaStateUsers = (data) => {}) => {
  const usersRef = getCollectionRef("users");
  const users = [];

  //query to select all users that does not contacted by admin
  const q = query(usersRef, where("isAlreadyContactedByAdmin", "==", false));

  try {
    onSnapshot(q, async (snapshot) => {
      snapshot.forEach((doc) => {
        const userData = { ...doc.data(), id: doc.id, savedProducts: [] };

        users.push(userData);
      });

      //store users in the global state
      globaStateUsers(users);
    });

    return { data: users };
  } catch (err) {
    console.log(err);
    return { error: "And error occured while getting no contacted users" };
  }
};

/**
 * change the propertie to check if the user is already contacted by admin
 * @param {String} userId
 * @returns data | error
 */
const changeUserAlreadyVisitedByAdmin = async (userId) => {
  const userRef = getDocumentRef("users", userId);

  try {
    await updateDoc(userRef, {
      isAlreadyContactedByAdmin: true,
    });
    return { data: true };
  } catch (err) {
    console.log(err);
    return {
      error: "And error occured while change user already visited by admin",
    };
  }
};

export {
  generateRecaptchaVerifier,
  sendOptCodeToTheUser,
  siginUserWithOtpCode,
  firebaseGetUser,
  updateUser,
  getUserWithUpdatedData,
  saveProduct,
  unsaveProduct,
  getUserNoContactedByAdmin,
  changeUserAlreadyVisitedByAdmin,
};
